import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Input, Button, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import { Trash2, Edit2 } from "react-feather";
import { http } from "../../http"; // <-- use direct API

const injectOnce = () => {
  if (document.getElementById("group-list-inline-css")) return;
  const s = document.createElement("style");
  s.id = "group-list-inline-css";
  s.textContent = `
    .gl-wrap{ border:1px solid #e5e7eb; border-radius:14px; background:#fff; overflow:auto; }
    .gl-table{ width:100%; border-collapse:collapse; }
    .gl-table thead th{ background:#f4f7f6; font-weight:800; font-size:12px; letter-spacing:.02em; padding:6px 8px; text-align:center; }
    .gl-table tbody td{ border-top:1px solid #f1f5f9; padding:8px; text-align:center; font-size:14px; }
    .pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:4px 10px; border-radius:999px; }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; cursor:pointer; }
    .chip-edit{ background:#d46f10; }
    .chip-del{ background:#ec1809; }
  `;
  document.head.appendChild(s);
};

export default function GroupList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/groups/list"); // ✅ direct URL
      setRows(Array.isArray(data?.Groups) ? data.Groups : []);
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    injectOnce();
    reload();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      [r?.groupName, r?.groupDesc].some((v) =>
        String(v || "")
          .toLowerCase()
          .includes(term)
      )
    );
  }, [rows, search]);

  const handleDelete = (id) => {
    swal("Warning", "Sure you want to delete this group?", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then(async (v) => {
      if (v === "delete") {
        try {
          await http.delete(`/groups/delete/${id}`); // ✅ direct URL
          setRows((prev) => prev.filter((x) => x._id !== id));
          swal("Deleted");
        } catch (e) {
          console.error(e);
          swal("Failed to delete");
        }
      }
    });
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <Row style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              background: "#f4f7f6",
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Groups <span className="pill">{filtered.length}</span>
              </div>
              <div style={{ flex: "1 1 240px" }} />
              <Input
                placeholder="Search groups..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ background: "#fff", maxWidth: 320 }}
              />
              <Button
                style={{
                  backgroundColor: "rgb(8, 91, 245)",
                  color: "#fff",
                  fontWeight: 600,
                  height: 32,
                }}
                onClick={() => navigate("/app/groups/create")}
              >
                Create Group
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            <div className="gl-wrap">
              <table className="gl-table">
                <thead>
                  <tr>
                    <th>NAME</th>
                    <th>DESCRIPTION</th>
                    <th>STATUS</th>
                    <th>PERMISSIONS COUNT</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length ? (
                    filtered.map((r) => (
                      <tr key={r?._id}>
                        <td>{r?.groupName || "-"}</td>
                        <td>{r?.groupDesc || "-"}</td>
                        <td
                          style={{
                            fontWeight: 700,
                            color:
                              r?.status === "Active" ? "#065f46" : "#7c2d12",
                          }}
                        >
                          {r?.status || "-"}
                        </td>
                        <td>
                          {(Array.isArray(r?.permissions)
                            ? r.permissions
                            : []
                          ).reduce(
                            (acc, p) => acc + (p?.permission?.length || 0),
                            0
                          )}
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              justifyContent: "center",
                            }}
                          >
                            <span
                              className="chip chip-edit"
                              title="Edit"
                              onClick={() =>
                                navigate(`/app/master/groups/edit/${r?._id}`)
                              }
                            >
                              <Edit2 size={14} color="#fff" />
                            </span>
                            <span
                              className="chip chip-del"
                              title="Delete"
                              onClick={() => handleDelete(r?._id)}
                            >
                              <Trash2 size={14} color="#fff" />
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} style={{ padding: 16, color: "#6b7280" }}>
                        No groups found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );
}
